<template>
  <div style="width: 100%;" v-loading.body="loading" element-loading-text="Loading..." class="gl-reporter">
    <b-container fluid id="options" ref="options">
      <el-row type="flex" align="middle" justify="end" class="headerRow">
        <el-col>
          <el-row type="flex" align="middle" justify="start" style="font-size: 1.5em;">
            OPEN ITEMS BY ACCOUNT REPORT
          </el-row>
        </el-col>
        <el-col>
          <el-row type="flex" align="middle" justify="end">
            <el-button v-if="disableReportRun">
              <b-spinner class="me-2" small /> Please Wait
            </el-button>
            <el-button plain @click="generateReport" :disabled="disableReportRun" v-else>
              Run report
            </el-button>
          </el-row>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Saved Report Options
        </el-col>
        <el-col :span="4">
          <el-select v-model="selectedOption" :filterable="true" @change="handleSelection" data-fieldtype="select" :multiple="false" clearable placeholder="Search">
              <el-option
                v-for="opt in savedReportOptions"
                :key="opt.id"
                :label="opt.report_name"
                :value="opt.id">
              </el-option>
            </el-select>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Report Options
        </el-col>
        <el-col :span="20">
          <el-button @click="openSaveOption">Save Report Options</el-button>
          <el-button @click="loadOptions">Load Report Options</el-button>
          <el-button @click="deleteOptions">Delete Report Options</el-button>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="end" class="headerRow">
        <el-col>
          <el-row type="flex" align="middle" justify="start" style="font-size: 1.2em;">
            Report Options
          </el-row>
        </el-col>
      </el-row>

      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Summary/Detail
        </el-col>
        <el-col :span="4">
          <el-radio-group v-model="reportOptions.type" @change="updateReportType">
            <el-radio-button value="detail">Detail</el-radio-button>
            <el-radio-button value="summary">Summary</el-radio-button>
          </el-radio-group>
        </el-col>
      </el-row>

      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Periods/Dates
        </el-col>
        <el-col :span="21">
          <el-radio-group v-model="reportOptions.dates" @change="updateReportDates">
            <el-radio-button value="datesAll">All</el-radio-button>
            <el-radio-button value="datesPeriod">Period</el-radio-button>
            <el-radio-button value="datesPosted">Posting Date</el-radio-button>
            <el-radio-button value="datesEffective">Effective Date</el-radio-button>
          </el-radio-group>
        </el-col>
      </el-row>

      <div v-if="reportOptions.dates==='datesPeriod'" class="m-top-20">
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3"></el-col>
          <el-col :span="4">Starting Period</el-col>
          <el-col :span="3"></el-col>
          <el-col :span="4">Ending Period</el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3"></el-col>
          <el-col :span="4">
            <el-select v-model="selectedCodes.period_start" :multiple="false" placeholder="First Period">
              <el-option
                v-for="opt in allCodes.periods"
                :key="opt.id"
                :label="opt.id + ' - ' + opt.start"
                :value="opt.id">
              </el-option>
            </el-select>
          </el-col>
          <el-col :span="3"></el-col>
          <el-col :span="4">
            <el-select v-model="selectedCodes.period_end" :multiple="false" placeholder="Last Period">
              <el-option
                v-for="opt in allCodes.periods"
                :key="opt.id"
                :label="opt.id + ' - ' + opt.start"
                :value="opt.id">
              </el-option>
            </el-select>
          </el-col>
        </el-row>
      </div>

      <div v-if="reportOptions.dates==='datesPosted'" class="m-top-20">
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3" />
          <el-col :span="4">Posting Date Start</el-col>
          <el-col :span="3" />
          <el-col :span="4">Posting Date End</el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3" />
          <el-col :span="4">
            <DateField v-model="selectedCodes.time_posted_start" :key="reRender" />
          </el-col>
          <el-col :span="3" />
          <el-col :span="4">
            <DateField v-model="selectedCodes.time_posted_end" :key="reRender" />
          </el-col>
        </el-row>
      </div>

      <div v-if="reportOptions.dates==='datesEffective'" class="m-top-20">
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3" />
          <el-col :span="4">Effective Date Start <sup>**</sup></el-col>
          <el-col :span="3" />
          <el-col :span="4">Effective Date End <sup>**</sup></el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3" />
          <el-col :span="4">
            <DateField v-model="selectedCodes.effective_date_start" :key="reRender"/>
          </el-col>
          <el-col :span="3" />
          <el-col :span="4">
            <DateField v-model="selectedCodes.effective_date_end" :key="reRender"/>
          </el-col>
        </el-row>
        <el-row v-if="reportOptions.dates==='datesEffective'" type="flex" align="middle" justify="start">
          <el-col :span="3" />
          <el-col :span="21"><p><sup>**</sup> Posting Date is used if Effective Date is not Set</p></el-col>
        </el-row>
      </div>

      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Company
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.company_id"
            :multiple="false" placeholder="All">
            <el-option
              v-for="opt in allCodes.companies"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="3" class="right">
          Production
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.production_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.productions"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
      </el-row>

      <el-row type="flex" align="middle" justify="start" class="selectRow">
        <el-col :span="3" class="right">
          Series
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.series_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.series"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="3" class="right">
          Location
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.location_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.locations"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="selectRow">
        <el-col :span="3" class="right">
          Set
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.set_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.sets"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="3" class="right">
          Free Field
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.free_field_1_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.freefield1"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Source Code
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.source_code_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.sourcecodes"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="3" class="right">
          Assets
        </el-col>
        <el-col :span="4">
          <el-select v-model="selectedCodes.asset" placeholder="All">
            <el-option value="true" label="Show Only Assets"></el-option>
            <el-option value="false" label="Hide Assets"></el-option>
            <el-option :value="undefined">All Transactions</el-option>
          </el-select>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow" v-show="currencies.length > 1">
        <el-col :span="3" class="right">
          Currency
        </el-col>
        <el-col :span="4">
          <el-select v-model="selectedCodes.currency_id" :multiple="true" placeholder="All">
            <el-option
              v-for="cur in currencies"
              :key="cur.id"
              :label="cur.code + ' - ' + parseFloat(cur.rate_vs_usd || 0).toFixed(4)"
              :value="cur.id">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="3" class="right">
          Convert To
        </el-col>
        <el-col :span="4">
          <el-select v-model="reportOptions.report_currency_id">
            <el-option
              v-for="opt in currencies"
              :key="opt.id"
              :label="opt.code + ' - ' + parseFloat(opt.rate_vs_usd || 0).toFixed(4)"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Report Output
        </el-col>
        <el-col :span="4">
          <el-select v-model="reportOptions.format">
            <el-option
              v-for="opt in reportOutputFormats"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="3" class="right">
          Include
        </el-col>
        <el-col :span="2" class="m-top-10">
          <el-checkbox v-model="reportOptions.includeDraftItems" @change="updateTransactionStatuses"></el-checkbox> Draft Items
        </el-col>
        <el-col :span="3" class="m-top-10">
          <el-checkbox v-model="reportOptions.includeUnpaidItems" @change="updateTransactionStatuses"></el-checkbox> Unpaid Items
        </el-col>
      </el-row>
      <el-row v-if="reportOptions.format === 'csv'" type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Subtotal by Account
        </el-col>
        <el-col :span="4" class="m-top-10">
          <el-checkbox v-model="csvSubTotalByAccount" /> 
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow"> 
        <el-col :span="3" class="right">
          Include
        </el-col>
        <el-col :span="1" class="m-top-10">
          <el-checkbox v-model="transactionTypes.pos.include"></el-checkbox> POs
        </el-col>
        <el-col :span="1" class="m-top-10">
          <el-checkbox v-model="transactionTypes.aps.include"></el-checkbox> APs
        </el-col>
        <el-col :span="1" class="m-top-10">
          <el-checkbox v-model="transactionTypes.jes.include"></el-checkbox> JEs
        </el-col>
        <el-col :span="1" class="m-top-10">
          <el-checkbox v-model="transactionTypes.pcs.include"></el-checkbox> PCs
        </el-col>
        <el-col :span="1" class="m-top-10">
          <el-checkbox v-model="transactionTypes.prs.include"></el-checkbox> PRs
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right"> Summarize Lines </el-col>
        <el-col :span="2" class="m-top-10">
          <el-checkbox v-model="reportOptions.summarizeLines" :disabled="!accounts.some(acct => acct.is_summarized)"></el-checkbox>
        </el-col>
        <el-col :span="6" class="left">
          <div class="nav-wrapper acc"><router-link to="/accounts" target="_blank">Click here to go to Accounts COA</router-link></div>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3"></el-col>
        <el-col :span="3">
          <el-radio :value="one_line" v-model="reportOptions.summaryType" v-show="reportOptions.summarizeLines">One Line</el-radio>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="end" class="section">
        <el-col>
          <el-row type="flex" align="middle" justify="start" style="font-size: 1.2em;">
            Vendor Selection
          </el-row>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="24" :offset="1">
          <el-radio-group v-model="reportOptions.vendorQuickPick" @change="updateVendors" :disabled="loading || disableReportRun">
            <el-radio-button value="custom">Custom</el-radio-button>
            <el-radio-button value="all">All</el-radio-button>
          </el-radio-group>
        </el-col>
      </el-row>
      <el-row v-show="reportOptions.vendorQuickPick==='custom'" type="flex" align="top" justify="start" class="headerRow" v-if="vendors && vendors.length > 0">
        <el-col :span="24" :offset="1">
          <el-transfer
            ref="vendorTransfer"
            v-model="selectedCodes.vendor_id"
            :data="vendors"
            :titles="[ 'Unselected Vendors', 'Selected Vendors' ]"
            :props="{ key: 'id', label: 'display_name' }"
            filterable
            filter-placeholder="Search Vendors"
          >
          </el-transfer>
        </el-col>
      </el-row>
      <div class="clear"></div>
      <el-row type="flex" align="middle" justify="end" class="section">
        <el-col>
          <el-row type="flex" align="middle" justify="start" style="font-size: 1.2em;">
            Account Selection
          </el-row>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="24" :offset="1">
          <el-radio-group v-model="reportOptions.accountQuickPick" @change="updateAccounts" :disabled="loading || disableReportRun">
            <el-radio-button value="custom">Custom</el-radio-button>
            <el-radio-button value="range">Range</el-radio-button>
            <el-radio-button value="all">All</el-radio-button>
            <el-radio-button value="expenses">Expenses Only</el-radio-button>
            <el-radio-button value="balance">Balance Sheet Only</el-radio-button>
            <el-radio-button value="none">None</el-radio-button>
          </el-radio-group>
        </el-col>
      </el-row>
      <div v-if="reportOptions.accountQuickPick==='range' && allCodes.accounts && allCodes.accounts.length > 0" class="m-top-20">
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3"></el-col>
          <el-col :span="4">From</el-col>
          <el-col :span="3"></el-col>
          <el-col :span="4">To</el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3"></el-col>
          <el-col :span="4">
            <el-select v-model="selectedCodes.account_id2" clearable :multiple="false" placeholder="First Account" :filterable="true" @change="checkCodeValidity">
              <el-option
                v-for="opt in allCodes.accounts"
                :key="opt.id"
                :label="opt.code + ' - ' + opt.name"
                :value="opt.id">
              </el-option>
            </el-select>
          </el-col>
          <el-col :span="3"></el-col>
          <el-col :span="4">
            <el-select v-model="selectedCodes.account_id1" clearable :multiple="false" placeholder="Last Account" :filterable="true" @change="checkCodeValidity">
              <el-option
                v-for="opt in filteredOptions2"
                :key="opt.id"
                :label="opt.code + ' - ' + opt.name"
                :value="opt.id">
              </el-option>
            </el-select>
          </el-col>
        </el-row>
      </div>

      <el-row v-show="reportOptions.accountQuickPick==='custom'" type="flex" align="top" justify="start" class="headerRow" v-if="allCodes.accounts && allCodes.accounts.length > 0">
        <el-col :span="24" :offset="1">
          <el-transfer
            ref="accountTransfer"
            v-model="selectedCodes.account_id"
            :data="accounts"
            :titles="[ 'Unselected Accounts', 'Selected Accounts' ]"
            :props="{ key: 'id', label: 'display_name' }"
            filterable
            filter-placeholder="Search Accounts"
          >
          </el-transfer>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="end" class="headerRow">
        <el-button v-if="disableReportRun">
          <b-spinner class="me-2" small /> Please Wait
        </el-button>
        <el-button plain @click="generateReport" :disabled="disableReportRun" v-else>
          Run report
        </el-button>
      </el-row>
    </b-container>
    <el-dialog
      title="Report Options"
      width="30%"
      v-model="visible"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <el-row type="flex" align="middle" element-loading-text='Saving...'>
        <el-col>
          <el-row type="flex" align="middle">
            <el-form :model="form" ref="authForm" v-if="selectedReportOptions">
              <el-form-item style="margin-bottom: 6px;">
                Do you wish to update the Report Options saved under {{ selectedReportOptions.report_name }}
              </el-form-item>
            </el-form>
            <el-form :model="form" ref="authForm" v-if="empty_error">
              <el-form-item style="margin-bottom: 6px;">
                Please select a set of Report Options to load.
              </el-form-item>
            </el-form>
            <el-form :model="form" ref="authForm" v-if="delete_error">
              <el-form-item style="margin-bottom: 6px;">
                Please select a set of Report Options to delete.
              </el-form-item>
            </el-form>
            <el-form :model="form" ref="authForm" v-if="!selectedReportOptions && !empty_error && !delete_error">
              <el-form-item style="margin-bottom: 6px;">
                <el-input placeholder="Name" v-model="savereportoptions" ref="nameField">
                </el-input>
              </el-form-item>
            </el-form>
          </el-row>
        </el-col>
      </el-row>
      <el-row v-if="error" type="flex" align="middle" justify="center" style="padding-top: 12px;">
        <el-tag type="danger">{{ error }}</el-tag>
      </el-row>
      <template v-slot:footer><span class="dialog-footer">
        <el-button @click="visible = false" class="auth-btn" v-if="!empty_error && !delete_error">
          Cancel
        </el-button>
        <el-button @click="saveOptions" class="auth-btn" type="primary">
          OK
        </el-button>
      </span></template>
    </el-dialog>
    <el-dialog
      title="Delete Report Options"
      width="30%"
      v-model="visible_delete"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false">
      <el-row type="flex" align="middle" element-loading-text='Saving...'>
        <el-col>
          <el-row type="flex" align="middle">
            <el-form :model="form" ref="authForm">
              <el-form-item style="margin-bottom: 6px;">
                Do you want to delete report options?
              </el-form-item>
            </el-form>
          </el-row>
        </el-col>
      </el-row>
      <el-row v-if="error" type="flex" align="middle" justify="center" style="padding-top: 12px;">
        <el-tag type="danger">{{ error }}</el-tag>
      </el-row>
      <template v-slot:footer><span class="dialog-footer">
        <el-button @click="visible_delete = false" class="auth-btn">
          Cancel
        </el-button>
        <el-button @click="confirmDeleteOptions" class="auth-btn" type="primary">
          OK
        </el-button>
      </span></template>
    </el-dialog>
  </div>
</template>

<script>
  /* eslint-disable no-unused-vars */
  import { markRaw } from 'vue'
  import { sortObjectByProperty } from '@/utils/helpers/ObjectSorting'
  import { capitalizeFirstLetter } from '@/utils/helpers/StringOperations'
  import { reportMixins } from '@/Mixins/Reports'
  import DateField from '@/components/InputComponents/DateField.vue'
  import { notify } from '@/utils/helpers/Notify'
  import moment from 'moment'
  import { getHeaders } from '@/utils/api'

  export default {
    name: 'OpenItemsByAccountReport',
    components: { DateField, notify },
    mixins: [ reportMixins ],
    data () {
      const { getJasperHost } = this.$store.getters
      return {
        csvSubTotalByAccount: false,
        filteredAccounts: [],
        isLoading: true,
        isSaving: false,
        isPrinting: false,
        isPrinted: false,
        scope: markRaw(this),
        width: '100%',
        pdfWidth: '100%',
        pdfHeight: '100%',
        displayOptions: {
          hideDecimals: false
        },
        reportOptions: {
          transactionStatuses: ['open', 'audited'],
          accountQuickPick: 'all',
          vendorQuickPick: 'all',
          type: 'detail',
          dates: 'datesAll',
          sort: 'account',
          status: 'all',
          format: 'pdf',
          currencyFactor: undefined,
          report_currency_id: 1,
          includeDraftItems: false,
          includeUnpaidItems: true,
          summarizeLines: false,
          summaryType: 'one_line'
        },
        selectedCodes: undefined,
        report_group: 'Open_Items_Account',
        savereportoptions: '',
        isBusy: false,
        visible: false,
        empty_error: false,
        delete_error: false,
        selectedReportOptions: null,
        savedReportOptions: [],
        selectedOption: null,
        visible_delete: false,
        form: {save_options: { label: '', value: '' }},
        newAddedReport: undefined,
        reRender: 0,
        allCodes: {},    // Show codes, e.g. accounts, series, locations, sets, etc.
        jasperSettings: {
          reportServer: `${getJasperHost}rest_v2/reports/Revolution/Phoenix/`,
          reportName: 'general_ledger_detail'
        },
        vendors: undefined,
        transactionTypes: undefined,
        reportOutputFormats: [
          { value: 'pdf', label: 'PDF' },
          { value: 'csv', label: 'CSV' }
        ],
        disableReportRun: false, // Disable run report button if false
        prettyJson: undefined,
        loading: false,
        summarization: { value: false, type: 'one_line' }
      }
    },

    computed: {
      filteredOptions2 () {
        return this.allCodes.accounts.filter((option) => option.id !== this.selectedCodes.account_id)
      },
      username () { return this.$store.getters.getUser.username },
      includedTypes () {
        if (this.selectedCodes.source_code_id.length) {
          this.transactionTypes.jes.include = true
        }
        return Object.values(this.transactionTypes).filter(types => types.include === true).map(type => type.name)
      },
      accounts () {
        const rawAccounts = Array.isArray(this.allCodes.accounts) ? this.allCodes.accounts : []
        return rawAccounts
          .filter((account) => account && account.type && account.type.toLowerCase() !== 'header')
          .map((account) => ({
            ...account,
            display_name: `${account.code} - ${account.name}`
          }))
      },
      currencies () {
        if (typeof this.allCodes.currencies !== 'undefined' && this.allCodes.currencies.length > 0) {
          return sortObjectByProperty(this.allCodes.currencies, 'id')
        } else {
          return []
        }
      },
      seriesCodes () {
        const selected = (this.allCodes.series || []).filter((ser) => this.selectedCodes.series_id.includes(ser.id))
        if (selected.length === 0) return 'All'
        else return selected.map((ser) => ser.code).join(', ')
      },
      locationCodes () {
        const selected = (this.allCodes.locations || []).filter((loc) => this.selectedCodes.location_id.includes(loc.id))
        if (selected.length === 0) return 'All'
        else return selected.map((loc) => loc.code).join(', ')
      },
      setCodes () {
        const selected = (this.allCodes.sets || []).filter((set) => this.selectedCodes.set_id.includes(set.id))
        if (selected.length === 0) return 'All'
        else return selected.map((set) => set.code).join(', ')
      },
      freeFieldCodes () {
        const selected = (this.allCodes.freefield1 || []).filter((ff) => this.selectedCodes.free_field_1_id.includes(ff.id) || this.selectedCodes.free_field_2_id.includes(ff.id) || this.selectedCodes.free_field_3_id.includes(ff.id) || this.selectedCodes.free_field_4_id.includes(ff.id))
        if (selected.length === 0) return 'All'
        else return selected.map((ff) => ff.code).join(', ')
      },
      sourceCodes () {
        const selected = (this.allCodes.sourcecodes || []).filter((sc) => this.selectedCodes.source_code_id.includes(sc.id))
        if (selected.length === 0) return 'All'
        else return selected.map((sc) => sc.code).join(', ')
      },
      seriesNames () {
        const selected = (this.allCodes.series || []).filter((ser) => this.selectedCodes.series_id.includes(ser.id))
        if (selected.length === 0) return ''
        else return selected.map((ser) => ser.name).join(', ')
      },
      locationNames () {
        const selected = (this.allCodes.locations || []).filter((loc) => this.selectedCodes.location_id.includes(loc.id))
        if (selected.length === 0) return ''
        else return selected.map((loc) => loc.name).join(', ')
      },
      setNames () {
        const selected = (this.allCodes.sets || []).filter((set) => this.selectedCodes.set_id.includes(set.id))
        if (selected.length === 0) return ''
        else return selected.map((set) => set.name).join(', ')
      },
      dateStart () {
        switch (this.reportOptions.dates) {
          case 'datesPeriod':
            return (this.allCodes.periods.find(({id}) => id === this.selectedCodes.period_start) || {}).start || '0000/00/00'

          case 'datesEffective':
            return this.selectedCodes.effective_date_start || '0000/00/00'

          case 'datesPosted':
            return this.selectedCodes.time_posted_start || '0000/00/00'

          default:
            return 'ALL'
        }
      },
      dateEnd () {
        switch (this.reportOptions.dates) {
          case 'datesPeriod':
            return (this.allCodes.periods.find(({id}) => id === this.selectedCodes.period_end) || {}).end || '9999/99/99'

          case 'datesEffective':
            return this.selectedCodes.effective_date_end || '9999/99/99'

          case 'datesPosted':
            return this.selectedCodes.time_posted_end || '9999/99/99'

          default:
            return 'ALL'
        }
      },
      hideDecimals () {
        return this.$data.displayOptions.hideDecimals
      },
      height () {
        // Reducing by 3 % to handle sigma-frontend navbar
        return window.innerHeight * 0.88 + 'px'
      }
    },
    methods: {
      checkCodeValidity () {
        const fromValue = this.selectedCodes.account_id2 !== '' ? this.allCodes.accounts.filter((account) => account.id === this.selectedCodes.account_id2) : this.allCodes.accounts.filter((element, index) => index === 0)
        const toValue = this.selectedCodes.account_id1 !== '' ? this.allCodes.accounts.filter((account) => account.id === this.selectedCodes.account_id1) : this.allCodes.accounts.filter((element, index) => index === this.allCodes.accounts.length - 1)
        if (fromValue && toValue) {
          if (fromValue[0].code > toValue[0].code) {
            this.disableReportRun = true
            notify(this, 'error', 'The To Account cannot be before the From Account. Please update your selection.')
          } else {
            if (this.selectedCodes.account_id1 === '' || this.selectedCodes.account_id2 === '') {
              this.selectedCodes.account_id = this.allCodes.accounts.map((account) => account.id)
            } else {
              this.disableReportRun = false
              this.filteredAccounts = this.allCodes.accounts.filter((account) => account.code >= fromValue[0].code && account.code <= toValue[0].code)
              this.selectedCodes.account_id = this.filteredAccounts.map((account) => account.id)
            }
          }
        }
      },
      resetComponent () {
        this.clearStore()
        this.resetFilters()
      },
      clearStore () {
        // TODO
      },
      updateReportType (type) {
        this.jasperSettings.reportName = 'general_ledger_' + type
      },
      updateReportDates (type) {
        this.selectedCodes.period_start = this.selectedCodes.period_end = this.selectedCodes.time_posted_start = this.selectedCodes.time_posted_end = this.selectedCodes.effective_date_start = this.selectedCodes.effective_date_end = undefined
      },
      updateTransactionStatuses () {
        this.reportOptions.transactionStatuses = ['open', 'audited']
        if (this.reportOptions.includeDraftItems) {
          this.reportOptions.transactionStatuses = ['draft', 'open', 'audited']
        }
      },
      updateAccounts (type) {
        if (!type) return false
        switch (type) {
          case 'none':
            this.disableReportRun = false
            this.selectedCodes.account_id = []
            break
          case 'all':
            this.disableReportRun = false
            this.selectedCodes.account_id = this.allCodes.accounts.map(acct => acct.id)
            break
          case 'expenses':
            this.disableReportRun = false
            this.selectedCodes.account_id = this.allCodes.accounts.filter(({type}) => type.toLowerCase().match('expense')).map(acct => acct.id)
            break
          case 'balance':
            this.disableReportRun = false
            this.selectedCodes.account_id = this.allCodes.accounts.filter(({type}) => {
              if (type.toLowerCase().match('asset') || type.toLowerCase().match('liability') || type.toLowerCase().match('income')) return true
            }).map(acct => acct.id)
            break
          case 'custom':
            this.disableReportRun = false
            break
          case 'range':
            this.disableReportRun = false
            break
          default:
            return undefined
        }
        // The transfer table is one tick behind.  Force update now
        this.$forceUpdate()
        return true
      },
      updateVendors (type) {
        if (!type) return false
        switch (type) {
          case 'none':
            this.disableReportRun = true
            this.selectedCodes.vendor_id = []
            break
          case 'all':
            this.disableReportRun = false
            this.selectedCodes.vendor_id = []
            break
          case 'custom':
            this.disableReportRun = false
            break
          default:
            return undefined
        }
        // The transfer table is one tick behind.  Force update now
        this.$forceUpdate()
        return true
      },
      addExpenseAccounts () {
        this.selectedCodes.account_id = this.allCodes.accounts.filter(({type}) => type.match('EXPENSE')).map(acct => acct.id)
      },
      selectReportFormat ({target}) {
        if (target.checked) {
          this.reportOptions.format = 'csv'
        } else {
          this.reportOptions.format = 'pdf'
        }
      },
      buildCsvData (sortedAccounts) {
        if (!sortedAccounts || !Array.isArray(sortedAccounts)) return false

        let detailedColumns = { 'Company': 'company_name', 'Production': 'production_name', 'Transaction Type': 'trans_type', 'Transaction ID': 'trans_number', 'Transaction Description': 'transaction_description', 'Transaction Line Description': 'line_desc', 'Account': 'account', 'Account Description': 'account_name', 'Vendor Code': 'transaction_vendor', 'Vendor Name': 'vendor_name', 'Series': 'series', 'Location': 'location', 'Set': 'set', 'FF1': 'free_field_1', 'FF2': 'free_field_2', 'FF3': 'free_field_3', 'FF4': 'free_field_4', 'Tax Code': 'tax_code', 'Asset': 'asset', 'Document Number': 'doc_number', 'Document Date': 'doc_date', 'Posting Date': 'transaction_time_posted', 'Effective Date': 'transaction_effective_date', 'Period': 'transaction_period_id', 'PO Number': 'po_number', 'Check Number': 'payment_number', 'Check Amount': 'transaction_payment_amount', 'Bank Recon': 'recon', 'Cur': 'transaction_currency', 'Amount': 'amount' }
        let summaryColumns = { 'Account': 'account_number', 'Account Description': 'account_desc', 'Amount': 'account_total' }

        // TODO: Sipra Output
        let columnMap = (this.reportOptions.type === 'summary') ? summaryColumns : detailedColumns

        return new Promise((resolve, reject) => {
          let ledgerData = []

          sortedAccounts.map(accountCode => {
            if (this.reportOptions.type === 'summary') {
              accountCode.transaction = undefined
              ledgerData.push(accountCode)
            } else {
              accountCode.transaction.map(row => ledgerData.push(row))
            }
          })

          let finalCsv
          this.disableReportRun = false
          ledgerData.map(data => {
            data.amount = data.amount ? parseFloat(data.amount).toFixed(2) : data.amount
            data.transaction_payment_amount = data.transaction_payment_amount ? parseFloat(data.transaction_payment_amount).toFixed(2) : data.transaction_payment_amount
          })
          if (this.csvSubTotalByAccount) { finalCsv = this.createGroupByAccountCsv(ledgerData, columnMap) } else { finalCsv = this.createCsv(ledgerData, columnMap) }

          if (finalCsv) resolve(finalCsv)
          else reject('Could not build CSV.')
        })
      },

      // The GL report header block, built from Vue view state. Extracted so the
      // server-side render path can send it verbatim (as `reportHeader`) and keep
      // the header labels byte-identical to the browser-built report.
      buildGlReportHeader () {
        return {
          report_title: `Open Items By Account ${capitalizeFirstLetter(this.reportOptions.type)}`,
          report_subtitle: '',
          report_name: 'general_ledger_detail',
          printed_by: this.username,
          date_run: this.today(),
          company: (this.selectedCodes.company_id) ? (this.allCodes.companies.find(({id}) => id === this.selectedCodes.company_id).name) : this.allCodes.companies[0].name,
          project:
            this.selectedCodes.production_id && this.selectedCodes.production_id.length
            ? this.allCodes.productions.filter((prod) => this.selectedCodes.production_id.includes(prod.id)).map((prod) => prod.name).join(', ')
            : this.allCodes.productions[0].name,
          currency: (this.reportOptions.report_currency_id) ? (this.allCodes.currencies.find(({id}) => id === this.reportOptions.report_currency_id).code) : this.currencies[0].code,
          period: this.dateStart + ' - ' + this.dateEnd,
          date_start: this.dateStart,
          date_end: this.dateEnd,
          grand_total: '',
          transactions: 'All',
          // Episodes is legacy
          episodes: (this.selectedCodes.series_id && this.selectedCodes.series_id.length > 0) ? (this.allCodes.series.find(({id}) => this.selectedCodes.series_id.includes(id)).code) : 'All',
          series: this.seriesCodes,
          seriesNames: this.seriesNames,
          locations: this.locationCodes,
          locationNames: this.locationNames,
          sets: this.setCodes,
          setNames: this.setNames,
          free_fields: this.freeFieldCodes,
          sourcecodes: this.sourceCodes,
          accounts: capitalizeFirstLetter(this.reportOptions.accountQuickPick),
          status: this.reportOptions.transactionStatuses.join(', '),
          data: []
        }
      },

      buildGlReport (sortedAccounts) {
        return new Promise((resolve, reject) => {
          let json = { report: this.buildGlReportHeader() }
          // Put finished data here
          let ledgerData = []
          // TODO Use selected accounts instead of all accounts
          this.allCodes.accounts.map((data) => {
            // Place account code into report
            let account = {}
            account.account_desc = data.name
            account.account_number = data.code
            account.account_id = data.id
            account.account_parent = data.parent
            // Now look through actual data and put into the proper group
            if (sortedAccounts.current[data.code]) {
              let transactions = []
              transactions = sortedAccounts.current[data.code]
              account.account_total = this.sumRecursive(transactions, 'amount', null, null, null, false)
              transactions.map((trn) => { trn.amount = parseFloat(trn.amount).toFixed(2) })
              account.transaction = transactions.sort((a, b) => a.trans_number.localeCompare(b.trans_number, 'en', { numeric: true }))
              ledgerData.push(account)
            }
          })
          json.report.grand_total = this.sumRecursive(ledgerData, 'account_total', null, null, null, false)
          json.report.data = ledgerData
          resolve(json)
        })
      },
      // Build a slim payload for the Jasper `addJson` call. The glbible rows
      // carry ~50 fields each, but the general_ledger_detail/summary template
      // only renders the ones below. Projecting to just these before the POST
      // (which JSON.stringifies the whole thing) is what keeps the serialization
      // from spiking ~1GB.
      //
      // This is DESTRUCTIVE on purpose: it frees each full transaction as soon
      // as its slim copy is made (and drops the source arrays), so the full and
      // slim datasets never fully coexist. That keeps the peak ~1x instead of
      // ~1.4x, which is what was crashing the tab before the request even fired.
      // Safe because `json` (and the resSorted it shares rows with) is discarded
      // right after this in the PDF path — nothing reads it again.
      buildJasperPayload (json) {
        const TXN_FIELDS = [
          'trans_number', 'trans_type', 'line_desc', 'vendor_name',
          'series', 'location', 'set',
          'free_field_1', 'free_field_2', 'free_field_3', 'free_field_4',
          'ins', 'tax_code', 'asset',
          'doc_number', 'transaction_effective_date', 'transaction_period_id',
          'po_number', 'payment_number', 'amount', 'transaction_currency'
        ]
        const src = json.report.data || []
        const data = []
        for (const account of src) {
          const txns = account.transaction || []
          const slimTxns = new Array(txns.length)
          for (let i = 0; i < txns.length; i++) {
            const trn = txns[i]
            const slimTrn = {}
            for (const field of TXN_FIELDS) slimTrn[field] = trn[field]
            slimTxns[i] = slimTrn
            txns[i] = null // release the full row now that it is copied
          }
          account.transaction = null // release the full row array
          data.push({
            account_number: account.account_number,
            account_desc: account.account_desc,
            account_total: account.account_total,
            transaction: slimTxns
          })
        }
        return { report: { ...json.report, data } }
      },
      generateReport () {
        this.loading = true
        this.disableReportRun = true
        const project =
            this.selectedCodes.production_id && this.selectedCodes.production_id.length
            ? this.allCodes.productions.filter((prod) => this.selectedCodes.production_id.includes(prod.id)).map((prod) => prod.name).join(', ')
            : this.allCodes.productions[0].name
        const fileName = `Open-Items-By-Account-${capitalizeFirstLetter(this.reportOptions.type)}_${project}_${moment.utc(new Date()).local().format('MM-DD-YYYY-HHmmss')}`
        this.summarization.value = this.reportOptions.summarizeLines
        if (this.reportOptions.format === 'csv') {
          this.getGlReportData()
            .then((resSorted) => {
              this.loading = false
              return this.buildGlReport(resSorted)
            })
            .then((resReport) => {
              return this.buildCsvData(resReport.report.data)
            })
            .then((resCsv) => {
              this.prettyJson = resCsv
              this.createNotification('Success', 'CSV is Ready. ', 'success', false, true, fileName)
              return true
            })
            .catch(error => {
              this.loading = false
              console.log(error)
            })
            .finally(() => {
              this.disableReportRun = false
              this.loading = false
            })
        } else {
          this.getGlReportData()
            .then((resSorted) => {
              return this.buildGlReport(resSorted)
            })
            .then(json => {
              return this.getJsonUrl(this.buildJasperPayload(json))
            })
            .then(resJsonUrl => {
              // PDF version of the report
              let report = this.jasperSettings.reportServer + this.jasperSettings.reportName + '.pdf?net.sf.jasperreports.json.source=' + resJsonUrl
              // HTML version of the report
              let preview = this.jasperSettings.reportServer + this.jasperSettings.reportName + '.html?net.sf.jasperreports.json.source=' + resJsonUrl
              return this.getReportUrl(report)
            })
            .then(reportUrl => {
              this.loading = false
              this.createNotification('Success', 'Report is Ready. ', 'success', reportUrl, false, fileName)
            })
            .catch(error => {
              this.loading = false
              console.log(error)
            })
            .finally(() => {
              this.disableReportRun = false
              this.loading = false
            })
        }
      },

      async saveOptions () {
        if (this.isBusy) return
        if (this.empty_error || this.delete_error) {
          this.visible = false
          this.empty_error = false
          this.delete_error = false
          return
        }
        this.isBusy = true
        if (this.selectedReportOptions) {
          await this.updateReportOptions({
            data: {
              report_group: this.report_group,
              report_name: this.selectedReportOptions.report_name,
              filter: { reportOptions: this.reportOptions, selectedCodes: this.selectedCodes, transactionTypes: this.transactionTypes }
            },
            id: this.selectedReportOptions.id })
        } else {
          if (!this.savereportoptions) {
            this.$notify({ title: 'Error', message: 'Report Name is required', type: 'error' })
            return
          }
          this.newAddedReport = await this.saveReportOptions({
            report_group: this.report_group,
            report_name: this.savereportoptions,
            filter: { reportOptions: this.reportOptions, selectedCodes: this.selectedCodes, transactionTypes: this.transactionTypes }
          })
        }
        this.visible = false
        this.$notify({ title: 'Success', message: 'Report Options Saved', type: 'success' })
        await this.getOptions()
        this.empty_error = false
        this.delete_error = false
        this.isBusy = false
        this.savereportoptions = ''
        this.selectedOption = this.newAddedReport.id
      },
      openSaveOption () {
        this.visible = true
      },
      async getOptions () {
        try {
          const savedReportOptions = await this.getReportOptions()
          this.savedReportOptions = savedReportOptions.results
        } catch (e) {
          console.log(e)
        }
      },
      async loadOptions () {
        if (this.selectedReportOptions && this.selectedReportOptions.id) {
          const loadReportOptions = await this.getReportOptions(this.selectedReportOptions.id)
          const { filter: { reportOptions, selectedCodes, transactionTypes } } = loadReportOptions
          if (!this.accounts.some(acct => acct.is_summarized) && reportOptions.summarizeLines) {
            reportOptions.summarizeLines = false
          }
          this.reportOptions = reportOptions
          setTimeout(() => {
            this.selectedCodes = selectedCodes
            this.transactionTypes = transactionTypes
            this.reRender++
          }, 10)
        } else {
          this.empty_error = true
          this.visible = true
        }
      },
      async deleteOptions () {
        try {
          if (this.selectedReportOptions) {
            this.visible_delete = true
          } else {
            this.delete_error = true
            this.visible = true
          }
        } catch (e) {
          console.log(e)
        }
      },
      async confirmDeleteOptions () {
        try {
          await this.deleteReportOptions(this.selectedReportOptions.id)
          this.savedReportOptions = this.savedReportOptions.filter(options => options.id !== this.selectedReportOptions.id)
          this.selectedReportOptions = null
          this.selectedOption = null
          this.resetFilters()
          this.getOptions()
          this.$notify({ title: 'Success', message: 'Report Options Deleted', type: 'success' })
        } catch (e) {
          console.log(e)
        }
        this.visible_delete = false
      },
      handleSelection (value) {
        this.selectedReportOptions = value ? this.savedReportOptions.find((s) => s.id === value) : null
        if (!value) this.resetFilters()
      },
      resetFilters () {
        this.reportOptions = {
          transactionStatuses: ['open', 'audited'],
          accountQuickPick: 'all',
          vendorQuickPick: 'all',
          type: 'detail',
          dates: 'datesAll',
          sort: 'account',
          status: 'all',
          format: 'pdf',
          currencyFactor: undefined,
          report_currency_id: 1,
          includeDraftItems: false,
          includeUnpaidItems: true,
          summarizeLines: false
        }
        this.selectedCodes = {
          account_id1: '',
          account_id2: '',
          period_start: undefined,
          period_end: undefined,
          time_posted_start: undefined,
          time_posted_end: undefined,
          effective_date_start: undefined,
          effective_date_end: undefined,
          account_id: [],
          series_id: [],
          location_id: [],
          set_id: [],
          free_field_1_id: [],
          free_field_2_id: [],
          free_field_3_id: [],
          free_field_4_id: [],
          company_id: undefined,
          production_id: [],
          source_code_id: [],
          asset: undefined,
          currency_id: [],
          vendor_id: []
        }
        this.transactionTypes = {     // Types of transactions and whether we need them or not
          'aps': { name: 'AP', include: true },
          'jes': { name: 'JE', include: true },
          'pcs': { name: 'PC', include: true },
          'prs': { name: 'PR', include: true },
          'pos': { name: 'PO', include: false }
        }
        this.summarization = { value: false, type: 'one_line' }
      }
    },
    watch: {
      hideDecimals (newValue) {
        this.gridApi.redrawRows()
      }
    },
    created () {
      this.loading = true
      this.disableReportRun = true
      this.resetFilters()
      this.getOptions()
      this.getCompanyCodes(['fetchAllCurrencies'])
        .then(() => {
          this.reportOptions.report_currency_id = this.allCodes.currencies.find((c) => c.is_base).id

          this.$store.dispatch('fetchAllVendorModels', { query: { page_size: 200 } })
            .then((response) => {
              this.vendors = sortObjectByProperty(response, 'code').map((v) => { return { display_name: v.code + ' - ' + v.name, id: v.id } })
              this.disableReportRun = false
            })
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    beforeUnmount () {
      this.resetComponent()
    }
  }
</script>

<style scoped>
  .gl-reporter .form-control:focus {
    box-shadow: none;
    border-color: #ced4da;
  }

  .gl-reporter .custom-select {
    background: #ffffff;
  }

  .gl-reporter .input-group .input-group-text {
    padding: 0 0.5rem 0 0;
    color: #ced4da;
    font-size: 0.875rem;
    background: #fff;
    border-left: none;
  }

  .white {
      color: #fff !important;
  }

  .black {
      color: #000 !important;
  }

  .bold {
      font-weight: 700 !important;
  }

  .clear {
      clear: both;
  }
  .small { font-size: 8px; }
  .right { float: right; text-align: right; padding: 0px 5px; }
  .left { float: left; text-align: left; padding: 0px 5px; }
  .selectRow { padding: 0px 20px; }
  .section { padding: 40px 20px 0px;  }
  .headerRow { padding: 15px 20px 0px; }
  .m-top-10 { margin-top: 10px; }
  .m-top-20 { margin-top: 20px; }
  .m-right-20 { margin-right: 20px; }
</style>

<style>
  .gl-reporter .el-select { display: block; }
  .el-transfer-panel { width:36%; max-width: 350px }
  .el-transfer-panel__body { min-height: 400px }
  .el-transfer-panel__list { min-height: 360px }
  .gl-reporter .acc { color: #007bff; text-decoration: underline; }
</style>
